<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import Skeleton from 'primevue/skeleton';
import type { ReportStatItem } from '@/composables/useReportsPayments';

interface Props {
    items: ReportStatItem[]
    activeId?: string | null
    loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    activeId: null,
    loading: false,
});

const emit = defineEmits<{
    'update:activeId': [id: string | null]
}>();

const toggle = (id: string) => {
    emit('update:activeId', props.activeId === id ? null : id);
};
</script>

<template>
    <div class="reports-stat-cards">
        <button
            v-for="item in items"
            :key="item.id"
            type="button"
            class="reports-stat-cards__card"
            :class="{ 'reports-stat-cards__card--active': activeId === item.id }"
            @click="toggle(item.id)"
        >
            <div class="reports-stat-cards__body">
                <span class="reports-stat-cards__label">{{ item.label }}</span>
                <Skeleton v-if="loading" width="55%" height="39px" border-radius="6px" />
                <span v-else class="reports-stat-cards__value">{{ item.value }}</span>
            </div>

            <div class="reports-stat-cards__icon-wrap" aria-hidden="true">
                <i :class="item.icon" class="reports-stat-cards__icon" />
            </div>
        </button>
    </div>
</template>

<style lang="scss" scoped>
.reports-stat-cards {
    display: flex;
    gap: 16px;

    &__card {
        display: flex;
        flex: 1 1 0;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        min-width: 0;
        height: 121px;
        padding: 0 28px;
        background: $white-color;
        border: 1px solid $border-gray-color;
        border-radius: 20px;
        cursor: pointer;
        transition: border-color 0.2s;

        &--active {
            border-color: #F9781C;
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: 15px;
        min-width: 0;
    }

    &__label {
        font-family: $font-family-archivo;
        font-size: 16px;
        font-weight: 400;
        line-height: 17px;
        color: rgba($black-color, 0.75);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
    }

    &__value {
        font-family: $font-family-archivo;
        font-size: 36px;
        font-weight: 600;
        line-height: 39px;
        color: $black-color;
        text-align: left;
    }

    &__icon-wrap {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 70px;
        height: 70px;
        border-radius: 20px;
        background: $border-gray-color;
        transition: background 0.2s;

        .reports-stat-cards__card--active & {
            background: linear-gradient(90deg, #A71C77 0%, #F9781C 100%);
        }
    }

    &__icon {
        font-size: 30px;
        line-height: 30px;
        color: $black-color;
        transition: color 0.2s;

        .reports-stat-cards__card--active & {
            color: $white-color;
        }
    }
}
</style>
